import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Users, UserPlus, Heart, Loader2, Search, Trash2,
  CheckCircle, Link2, Calendar, ChevronDown, RefreshCw, AlertCircle
} from 'lucide-react';

export default function AssignmentsManagement() {
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    
    try {
      // Fetch all data in parallel
      const results = await Promise.allSettled([
        api.get('/clients?status=active'),
        api.get('/users'),
        api.get('/clients/all-assignments')
      ]);
      
      // Process clients
      if (results[0].status === 'fulfilled') {
        setClients(results[0].value.data || []);
      } else {
        console.error('Failed to fetch clients:', results[0].reason);
      }
      
      // Process employees - include more roles
      if (results[1].status === 'fulfilled') {
        const caregivers = (results[1].value.data || []).filter(u => 
          ['caregiver', 'dsp', 'lpn', 'rn', 'admin', 'staff', 'cna'].includes(u.role?.toLowerCase())
        );
        setEmployees(caregivers);
      } else {
        console.error('Failed to fetch users:', results[1].reason);
      }
      
      // Process assignments
      if (results[2].status === 'fulfilled') {
        setAssignments(results[2].value.data || []);
      } else {
        console.error('Failed to fetch assignments:', results[2].reason);
      }

      // Check if any critical data failed
      const allFailed = results.every(r => r.status === 'rejected');
      if (allFailed) {
        setLoadError('Failed to load data. Please check your connection and try again.');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setLoadError('An unexpected error occurred. Please try again.');
    } finally {
      // Safety valve: always clear loading state
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const fetchAssignments = async () => {
    try {
      const response = await api.get('/clients/all-assignments');
      setAssignments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      setAssignments([]);
    }
  };

  const handleCreateAssignment = async () => {
    if (!selectedClient || !selectedEmployee) {
      toast.error('Please select both a client and an employee');
      return;
    }
    
    setCreating(true);
    try {
      await api.post(`/clients/${selectedClient}/assign`, {
        employee_id: selectedEmployee
      });
      
      const client = clients.find(c => c.id === selectedClient);
      const employee = employees.find(e => e.id === selectedEmployee);
      
      toast.success(
        <div>
          <p className="font-semibold">Assignment Created!</p>
          <p className="text-sm text-gray-600">
            {employee?.full_name || employee?.email} → {client?.first_name} {client?.last_name}
          </p>
        </div>
      );
      
      setSelectedClient('');
      setSelectedEmployee('');
      fetchAssignments();
    } catch (error) {
      console.error('Failed to create assignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveAssignment = async (clientId, employeeId, clientName, employeeName) => {
    const confirmKey = `${clientId}-${employeeId}`;
    setRemovingId(confirmKey);
    
    try {
      await api.delete(`/clients/${clientId}/assign/${employeeId}`);
      toast.success(`Removed ${employeeName} from ${clientName}`);
      fetchAssignments();
    } catch (error) {
      console.error('Failed to remove assignment:', error);
      toast.error('Failed to remove assignment');
    } finally {
      setRemovingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  // Filter assignments based on search
  const filteredAssignments = assignments.filter(a => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      a.client_name?.toLowerCase().includes(query) ||
      a.employee_name?.toLowerCase().includes(query)
    );
  });

  // Group assignments by client for better organization
  const groupedByClient = filteredAssignments.reduce((acc, assignment) => {
    const clientId = assignment.client_id;
    if (!acc[clientId]) {
      acc[clientId] = {
        client_id: clientId,
        client_name: assignment.client_name,
        assignments: []
      };
    }
    acc[clientId].assignments.push(assignment);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 gap-3">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        <p className="text-gray-500 text-sm">Loading assignments...</p>
      </div>
    );
  }

  // Show error state with Try Again button
  if (loadError || (clients.length === 0 && employees.length === 0 && assignments.length === 0)) {
    return (
      <div className="flex flex-col justify-center items-center h-64 gap-4">
        <div className="p-4 bg-red-50 rounded-full">
          <AlertCircle className="w-12 h-12 text-red-400" />
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {loadError || 'No data available'}
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            There was a problem loading the assignments data.
          </p>
        </div>
        <Button
          onClick={loadInitialData}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="retry-load-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-xl">
            <Link2 className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Client Assignments
            </h2>
            <p className="text-gray-600">Assign caregivers to clients for EMR charting access</p>
          </div>
        </div>
        <div className="text-right">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full text-blue-700 font-medium">
            <CheckCircle className="w-4 h-4" />
            {assignments.length} active assignments
          </span>
        </div>
      </div>

      {/* Create New Assignment Card */}
      <Card className="p-6 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
        <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-purple-600" />
          Create New Assignment
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Client Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Client
            </label>
            <div className="relative">
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white"
                data-testid="assignment-client-select"
              >
                <option value="">Choose a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.first_name} {client.last_name} {client.city ? `(${client.city})` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Employee Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Staff Member
            </label>
            <div className="relative">
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white"
                data-testid="assignment-employee-select"
              >
                <option value="">Choose an employee...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name || emp.email} ({emp.role?.toUpperCase()})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Create Button */}
          <div className="flex items-end">
            <Button
              onClick={handleCreateAssignment}
              disabled={!selectedClient || !selectedEmployee || creating}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3"
              data-testid="create-assignment-btn"
            >
              {creating ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-5 h-5 mr-2" />
              )}
              Assign to Client
            </Button>
          </div>
        </div>
      </Card>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by client or employee name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          data-testid="assignment-search"
        />
      </div>

      {/* Assignments List */}
      {Object.keys(groupedByClient).length === 0 ? (
        <Card className="p-12 text-center border-purple-100">
          <Link2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Assignments Yet</h3>
          <p className="text-gray-600">
            Use the form above to assign caregivers to clients
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.values(groupedByClient).map((group) => (
            <Card key={group.client_id} className="border-purple-100 overflow-hidden">
              {/* Client Header */}
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 px-6 py-4 border-b border-purple-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-100 rounded-full">
                    <Heart className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{group.client_name}</h4>
                    <p className="text-sm text-gray-600">
                      {group.assignments.length} caregiver{group.assignments.length !== 1 ? 's' : ''} assigned
                    </p>
                  </div>
                </div>
              </div>

              {/* Assigned Employees */}
              <div className="divide-y divide-gray-100">
                {group.assignments.map((assignment) => (
                  <div 
                    key={assignment.id} 
                    className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <Users className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{assignment.employee_name}</p>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span>{assignment.employee_email}</span>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Assigned {formatDate(assignment.assigned_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveAssignment(
                        assignment.client_id, 
                        assignment.employee_id,
                        group.client_name,
                        assignment.employee_name
                      )}
                      disabled={removingId === `${assignment.client_id}-${assignment.employee_id}`}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`remove-assignment-${assignment.id}`}
                    >
                      {removingId === `${assignment.client_id}-${assignment.employee_id}` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
